import React, { useState, useEffect } from 'react'
import { Box, Button, Card, CardContent, Grid, Typography, Chip } from '@mui/material'
import { useForm } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { format, subDays } from 'date-fns'
import FormProvider from '../../../common/hook-form/FormProvider'
import RHFTextField from '../../../common/hook-form/RHFTextField'
import RHFDateRangePickerField from '../../../common/hook-form/RHFDateRangePickerField'
import { MerchantFilterCached } from '../../../common/ProshipFormComponents'
import ProzoDataGrid from '../../../common/ProzoDataGrid'
import { apiConstants, post } from '../../../common/constants'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined'

const PAGE_SIZE = 20

const ATTRIBUTE_COLORS = {
  piiAccess: 'warning',
  unmaskedAddressAccess: 'info',
}

function BoolChip({ value }) {
  return (
    <Chip
      label={value ? 'True' : 'False'}
      size="small"
      color={value ? 'success' : 'error'}
      variant="outlined"
    />
  )
}

export default function PiiAccessTab() {
  const { enqueueSnackbar } = useSnackbar()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(PAGE_SIZE)
  const [rowCount, setRowCount] = useState(0)
  const [filters, setFilters] = useState(null)
  

  const methods = useForm({
    defaultValues: {
      merchant: null,
      email: '',
      fromDate: subDays(new Date(), 7),
      toDate: new Date(),
    },
  })

  const { handleSubmit, reset } = methods

  const handleReset = () => {
    reset({ merchant: null, email: '', fromDate: subDays(new Date(), 7), toDate: new Date() })
    setRows([])
    setSubmitted(false)
    setPage(0)
    setFilters(null)
  }

  const onSubmit = (data) => {
    setPage(0)
    setFilters({
      merchantId: data.merchant || undefined,
      email: data.email?.trim() || undefined,
      fromDate: data.fromDate ? format(new Date(data.fromDate), 'dd-MM-yyyy') : undefined,
      toDate: data.toDate ? format(new Date(data.toDate), 'dd-MM-yyyy') : undefined,
    })
  }

  useEffect(() => {
    if (!filters) return
    setLoading(true)
    post(apiConstants.AUDIT_PII_ACCESS, { ...filters, offset: page * sizePerPage, limit: sizePerPage })
      .then((res) => {
        const list = res?.data?.data || []
        setRows(list.map((r, i) => ({ ...r, id: i })))
        setRowCount(res?.data?.count || 0)
        setSubmitted(true)
      })
      .catch(() => enqueueSnackbar('Failed to fetch PII access history', { variant: 'error' }))
      .finally(() => setLoading(false))
  }, [filters, page, sizePerPage]) // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    {
      field: 'auditTime',
      headerName: 'Time',
      flex: 1.2,
      renderCell: (p) => (
        <Typography variant="body2">
          {p.value ? format(new Date(p.value), 'dd MMM yyyy, HH:mm') : '—'}
        </Typography>
      ),
    },
    {
      field: 'fullName',
      headerName: 'User',
      flex: 1.5,
      renderCell: (p) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>{p.value || '—'}</Typography>
          <Typography variant="caption" color="text.secondary">{p.row.email || ''}</Typography>
        </Box>
      ),
    },
    {
      field: 'attributeName',
      headerName: 'Attribute',
      flex: 1.5,
      renderCell: (p) =>
        p.value ? (
          <Chip
            label={p.value}
            size="small"
            color={ATTRIBUTE_COLORS[p.value] || 'default'}
          // variant="outlined"
          />
        ) : (
          <Typography variant="body2">—</Typography>
        ),
    },
    {
      field: 'oldValue',
      headerName: 'Old Value',
      flex: 0.8,
      renderCell: (p) => <BoolChip value={p.value} />,
    },
    {
      field: 'newValue',
      headerName: 'New Value',
      flex: 0.8,
      renderCell: (p) => <BoolChip value={p.value} />,
    },
    {
      field: 'username',
      headerName: 'Modified By',
      flex: 0.8,
      renderCell: (p) => <Typography variant="body2">{p.value || '—'}</Typography>,
    },
  ]

  return (
    <Box>
      <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: 'none' }}>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fafafa', borderBottom: '1px solid', borderColor: 'divider' }}>
          <FilterAltOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.primary' }}>Search Filters</Typography>
        </Box>
        <CardContent>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <MerchantFilterCached fullWidth />
              </Grid>
              <Grid item xs={12} md={3}>
                <RHFTextField name="email" label="Email" placeholder="user@example.com" />
              </Grid>
              <Grid item xs={12} md={3}>
                <RHFDateRangePickerField label="Date Range" />
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button type="submit" variant="contained" size="medium" disabled={loading} startIcon={<SearchOutlinedIcon />}>
                    Search
                  </Button>
                  <Button type="button" variant="outlined" size="medium" onClick={handleReset} startIcon={<RestartAltOutlinedIcon />}>
                    Reset
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </FormProvider>
        </CardContent>
      </Card>

      {submitted && (
        <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: 'none' }}>
          <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fafafa' }}>
            <Typography variant="subtitle2" fontWeight={600}>Results</Typography>
            <Chip label={`${rowCount} records`} size="small" color="primary" variant="outlined" />
          </Box>
          {rows.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <InboxOutlinedIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
              <Typography variant="subtitle1" color="text.secondary" fontWeight={500}>No records found</Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>Try adjusting your filters and search again</Typography>
            </Box>
          ) : (
            <CardContent sx={{ px: 0, pb: '0 !important' }}>
              <ProzoDataGrid
                rows={rows}
                columns={columns}
                loading={loading}
                rowCount={rowCount}
                page={page}
                setPage={setPage}
                sizePerPage={sizePerPage}
                setSizePerPage={setSizePerPage}
                paginationMode="server"
                disableColumnMenu
                disableColumnFilter
                getRowHeight={() => 40}
              />
            </CardContent>
          )}
        </Card>
      )}
    </Box>
  )
}
